import { Eye, EyeOff, Shapes } from "lucide-react";

import type { GraphicFamily } from "../api/types";
import { ChoiceCard, PanelGroup, PanelRow } from "../components/panel";
import { Motion, Presence } from "../components/presence";
import { SaveDossierHint } from "../components/SaveDossierHint";
import { useMediaQuery } from "../components/useMediaQuery";
import { FloatingPanel, IconButton } from "../components/ui";
import { HelpButton } from "../help/HelpButton";
import { t } from "../i18n/i18n";
import { term } from "../i18n/vocabulary";
import { GraphicGlyph } from "../map/graphics/GraphicGlyph";
import { allGraphics, labelOf, typeOf, useGraphics, viewOf } from "../map/graphics/useGraphics";
import { type Draft, useUi } from "../state/ui";
import { useLeavingList } from "./leavingList";

/** Graphiques tactiques du dossier (« Objets de la mission ») : palette des types à tracer sur la carte — zones,
 *  lignes et panaches NRBC du bac à sable de l'iPad, aux couleurs réglementaires de leur camp (APP-6) — puis
 *  liste des graphiques tracés, qui ouvre leur fiche. */

type Palette = Omit<Draft, "points" | "hover">;

/** Type de graphique à tracer : carte de choix (abréviation, nom), sélectionnée pendant son tracé. */
function TypeCard({
  family,
  kind,
  short,
  name,
  color,
  active,
  onClick,
}: {
  family: GraphicFamily;
  kind: string;
  short: string;
  name: string;
  color: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <ChoiceCard
      selected={active}
      onClick={onClick}
      icon={<GraphicGlyph family={family} kind={kind} color={color} size={16} />}
      title={short}
      detail={name}
    />
  );
}

export function GraphicsPanel() {
  const { graphics, catalog, canEdit, guard } = useGraphics();
  const draft = useUi((s) => s.draft);
  const setDraft = useUi((s) => s.setDraft);
  const select = useUi((s) => s.select);
  const selection = useUi((s) => s.selection);
  const requestFlyTo = useUi((s) => s.requestFlyTo);
  // Consigne au doigt sur tablette (« Terminer » finit une ligne : le double toucher n'est pas garanti).
  const coarse = useMediaQuery("(pointer: coarse)");
  const visible = useUi((s) => s.controlVisible.graphics !== false);
  const setControlVisible = useUi((s) => s.setControlVisible);
  const items = allGraphics(graphics);
  // Un graphique supprimé sort en fondu, à sa place (puis la liste se resserre).
  const entries = useLeavingList(items, (item) => item.graphic.id);
  const selectedId = selection?.layer === "graphic" ? selection.id : null;

  /** Choix d'un type : le tracé commence (ou s'arrête, second clic sur le même type). */
  const start = (palette: Palette) => {
    if (draft && draft.family === palette.family && draft.kind === palette.kind) {
      setDraft(null);
      return;
    }
    if (!guard(t("dessiner des graphiques"))) return;
    if (!visible) setControlVisible("graphics", true);
    select(null);
    setDraft({ ...palette, points: [], hover: null });
  };
  const active = (family: GraphicFamily, kind: string) => draft?.family === family && draft.kind === kind;

  return (
    <FloatingPanel
      enter="left"
      width="view"
      icon={Shapes}
      title={t("Graphiques tactiques")}
      actions={
        <>
          <IconButton
            size="sm"
            onClick={() => setControlVisible("graphics", !visible)}
            title={visible ? t("Masquer les graphiques sur la carte") : t("Afficher les graphiques sur la carte")}
            aria-label={visible ? t("Masquer les graphiques sur la carte") : t("Afficher les graphiques sur la carte")}
            aria-pressed={!visible}
          >
            {visible ? <Eye size={16} /> : <EyeOff size={16} />}
          </IconButton>
          <HelpButton screen="carte" topic="carte.graphiques" buttonSize="sm" size={16} />
        </>
      }
    >
      <Presence show={!canEdit}>
        <Motion origin="fade">
          <SaveDossierHint action="dessiner des graphiques" variant="banner" />
        </Motion>
      </Presence>
      <PanelGroup title={t("Zones")} flush>
        <div className="grid grid-cols-2 gap-1.5 px-4 pb-1">
          {catalog?.zones.map((z) => (
            <TypeCard
              key={z.kind}
              family="operationalGraphics"
              kind={z.kind}
              short={z.short}
              name={term(z.name)}
              color={z.color}
              active={active("operationalGraphics", z.kind)}
              onClick={() =>
                start({ family: "operationalGraphics", kind: z.kind, name: term(z.name), color: z.color, radius_m: z.radius_m })
              }
            />
          ))}
        </div>
      </PanelGroup>
      <PanelGroup title={t("Lignes")} flush>
        <div className="grid grid-cols-2 gap-1.5 px-4 pb-1">
          {catalog?.lines.map((l) => (
            <TypeCard
              key={l.kind}
              family="lineGraphics"
              kind={l.kind}
              short={l.short}
              name={term(l.name)}
              color={l.color}
              active={active("lineGraphics", l.kind)}
              onClick={() => start({ family: "lineGraphics", kind: l.kind, name: term(l.name), color: l.color })}
            />
          ))}
        </div>
      </PanelGroup>
      <PanelGroup title={t("Panaches NRBC")} flush>
        <div className="grid grid-cols-2 gap-1.5 px-4 pb-1">
          {catalog?.agents.map((a) => (
            <TypeCard
              key={a.kind}
              family="cbrnPlumes"
              kind={a.kind}
              short={t("NRBC {letter}", { letter: a.letter })}
              name={term(a.name)}
              color={a.color}
              active={active("cbrnPlumes", a.kind)}
              onClick={() =>
                start({
                  family: "cbrnPlumes",
                  kind: a.kind,
                  name: t("NRBC {agent}", { agent: term(a.name).toLowerCase() }),
                  color: a.color,
                  radius_m: a.released_m,
                  downwind_m: a.downwind_m,
                })
              }
            />
          ))}
        </div>
        {canEdit && (
          <p className="px-4 pt-2 pb-1 text-meta leading-snug text-ink-muted">
            {coarse
              ? t("Zones et panaches : touchez la carte. Lignes : touchez chaque point, puis « Terminer ».")
              : t("Zones et panaches : un clic sur la carte. Lignes : un clic par point, double-clic sur le dernier pour terminer.")}
          </p>
        )}
      </PanelGroup>

      <PanelGroup title={t("Dans le dossier")} count={items.length}>
        {entries.length === 0 ? (
          <p className="px-2 pb-1 text-ui leading-snug text-ink-muted">
            {t("Aucun graphique. Choisissez un type ci-dessus, puis tracez-le sur la carte.")}
          </p>
        ) : (
          <ul className="space-y-px pb-1">
            {entries.map(({ key, item, present }) => {
              const type = typeOf(catalog, item);
              const kind = item.family === "cbrnPlumes" ? item.graphic.agent : item.graphic.kind;
              const view = viewOf(catalog, item);
              return (
                <Presence key={key} show={present}>
                  <Motion origin="fade" role="listitem">
                    <PanelRow
                      icon={<GraphicGlyph family={item.family} kind={kind} color={type.color} />}
                      title={labelOf(catalog, item)}
                      detail={term(type.name)}
                      selected={selectedId === item.graphic.id}
                      onClick={() => {
                        select({ layer: "graphic", id: item.graphic.id, lngLat: view.at });
                        requestFlyTo(view.at, view.zoom);
                      }}
                    />
                  </Motion>
                </Presence>
              );
            })}
          </ul>
        )}
      </PanelGroup>
    </FloatingPanel>
  );
}
